// File: src/services/media/contracts/mediaRefV1.ts
//
// MediaRefV1 — canonical media reference contract
// -----------------------------------------------
// Goals:
// - One stable, auditable reference shape for all media providers
// - Pure types + tiny normalizers (no side effects)
// - Used by export/runtime resolvers (authority-safe)
//
// Providers (v1):
// - url      : absolute URL (http/https/blob/data)
// - static   : app-bundled or public asset path (resolved via Vite BASE_URL)
// - firebase : Firebase Storage path (getDownloadURL(ref(getStorage(), path)))
// - gcs      : GCS location (gs://bucket/object or bucket+object)
// - registry : abstract key resolved by an injected registry resolver

export const MEDIA_REF_V1 = 1 as const;

export type MediaProviderV1 = "url" | "static" | "firebase" | "gcs" | "registry";

export type MediaKindV1 = "audio" | "video" | "image" | "binary";

/** Normalize string-ish fields (trim, coerce to string, fallback). */
export function normStr(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  const s = String(v).trim();
  return s.length ? s : fallback;
}

/** Normalize kind-ish fields. Defaults to "binary" for safety. */
export function normKind(
  v: unknown,
  fallback: MediaKindV1 = "binary",
): MediaKindV1 {
  const s = normStr(v).toLowerCase();
  if (s === "audio" || s === "video" || s === "image" || s === "binary") return s;
  return fallback;
}

/** Normalize provider-ish fields. Returns null if not a known provider. */
export function normProvider(v: unknown): MediaProviderV1 | null {
  const s = normStr(v).toLowerCase();
  if (s === "url" || s === "static" || s === "firebase" || s === "gcs" || s === "registry") return s;
  return null;
}

export interface MediaRefBaseV1 {
  v: 1;
  provider: MediaProviderV1;
  kind: MediaKindV1;
  /** Optional hint for downstream (content-type). */
  mime?: string;
  /** Optional label/debug tag (not required for determinism). */
  label?: string;
}

export interface UrlMediaRefV1 extends MediaRefBaseV1 {
  provider: "url";
  url: string; // absolute URL preferred; blob/data allowed
}

export interface StaticMediaRefV1 extends MediaRefBaseV1 {
  provider: "static";
  path: string; // "/assets/a.wav" or "audio/a.wav" (resolved via Vite BASE_URL)
}

export interface FirebaseMediaRefV1 extends MediaRefBaseV1 {
  provider: "firebase";
  path: string; // Storage object path, e.g. "media/audio/foo.wav"
}

export interface GcsMediaRefV1 extends MediaRefBaseV1 {
  provider: "gcs";
  /** Prefer gs://bucket/object; or provide bucket+object. */
  gsUri?: string;
  bucket?: string;
  object?: string;
}

export interface RegistryMediaRefV1 extends MediaRefBaseV1 {
  provider: "registry";
  key: string; // registry key resolved by injected resolver
  /** Optional namespace (helps multi-registry installs). */
  registry?: string;
}

export type MediaRefV1 =
  | UrlMediaRefV1
  | StaticMediaRefV1
  | FirebaseMediaRefV1
  | GcsMediaRefV1
  | RegistryMediaRefV1;

type NormalizeOpts = {
  fallbackKind?: MediaKindV1;
};

/**
 * Best-effort validator/normalizer for arbitrary input.
 * - Does NOT fabricate missing required fields.
 * - Coerces obvious aliases (href/src/storagePath/objectPath/etc.).
 * - Returns null if the input cannot be safely interpreted as MediaRefV1.
 */
export function normalizeMediaRefV1(
  input: unknown,
  opts: NormalizeOpts = {},
): MediaRefV1 | null {
  if (!input || typeof input !== "object") return null;
  const any = input as any;

  const v = MEDIA_REF_V1;
  const provider = normProvider(any.provider);
  if (!provider) return null;

  const kind = normKind(any.kind, opts.fallbackKind ?? "binary");
  const mime = normStr(any.mime ?? any.contentType ?? any.type, "");
  const label = normStr(any.label, "");

  const base = {
    v,
    provider,
    kind,
    ...(mime ? { mime } : {}),
    ...(label ? { label } : {}),
  } as const;

  switch (provider) {
    case "url": {
      const url = normStr(any.url ?? any.href, "");
      if (!url) return null;
      return { ...base, provider: "url", url };
    }

    case "static": {
      const path = normStr(any.path ?? any.src, "");
      if (!path) return null;
      return { ...base, provider: "static", path };
    }

    case "firebase": {
      const path = normStr(any.path ?? any.storagePath ?? any.objectPath, "");
      if (!path) return null;
      return { ...base, provider: "firebase", path };
    }

    case "gcs": {
      const gsUri = normStr(any.gsUri ?? any.gsURL ?? any.gcsUri, "");
      const bucket = normStr(any.bucket, "");
      const object = normStr(any.object ?? any.path, "");

      if (!gsUri && !(bucket && object)) return null;

      return {
        ...base,
        provider: "gcs",
        ...(gsUri ? { gsUri } : {}),
        ...(bucket ? { bucket } : {}),
        ...(object ? { object } : {}),
      };
    }

    case "registry": {
      const key = normStr(any.key ?? any.id, "");
      if (!key) return null;

      const registry = normStr(any.registry ?? any.ns, "");
      return {
        ...base,
        provider: "registry",
        key,
        ...(registry ? { registry } : {}),
      };
    }

    default:
      return null;
  }
}

